import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useFilters } from '../../../../hooks/useFilters';
import { IMembershipPlanPriceModel } from '../../../../models/MembershipPlanPriceModel';
import { SelectOption } from '../../../../models/SelectOption';

import { subscriptionsListSelector } from '../../../../store/redux-slices/subscriptions/common/selectors';
import { formatDate } from '../../../../utils/dateHelpers';
import { SRMembersListFilters } from './srMembersFilters.models';

type UseSRMembersFiltersProps = {
  standardPricing?: IMembershipPlanPriceModel[];
};

export const useSRMembersFilters = ({
  standardPricing,
}: UseSRMembersFiltersProps) => {
  const subscriptionsList = useSelector(subscriptionsListSelector);

  const { addFilter, filters } = useFilters<SRMembersListFilters>();

  const onFilterChangeHandler = useCallback(
    (evt: any, filterType: SRMembersListFilters) => {
      switch (filterType) {
        case SRMembersListFilters.Search:
          addFilter(SRMembersListFilters.Search, evt.target.value);
          return;
        case SRMembersListFilters.Plan:
          addFilter(
            SRMembersListFilters.Plan,
            evt ? evt.map((x: SelectOption) => x.value) : null,
          );
          return;
        case SRMembersListFilters.MemberType:
          addFilter(
            SRMembersListFilters.MemberType,
            evt ? evt.map((x: SelectOption) => x.value) : null,
          );
          return;
        case SRMembersListFilters.RenewStatus:
          addFilter(SRMembersListFilters.RenewStatus, evt ? evt.value : null);
          return;
        case SRMembersListFilters.SubscriptionId:
          addFilter(
            SRMembersListFilters.SubscriptionId,
            evt ? evt.map((x: SelectOption) => x.value) : null,
          );
          return;
        case SRMembersListFilters.OversubscribedOnly:
          addFilter(
            SRMembersListFilters.OversubscribedOnly,
            evt.target.checked,
          );
      }
    },
    [addFilter],
  );

  const plansOptions = useMemo(
    () =>
      standardPricing?.map((plan) => ({
        label: plan.membershipPlanName,
        value: plan.membershipPlanName,
      })),
    [standardPricing],
  );
  const subscriptionsListOptions = useMemo(
    () =>
      subscriptionsList?.map((subscription) => ({
        label: `SR ${formatDate(subscription.effectiveDateFrom)} - ${formatDate(
          subscription.effectiveDateTo,
        )}`,
        value: subscription.id,
      })),
    [subscriptionsList],
  );

  return {
    plansOptions,
    filters,
    onFilterChangeHandler,
    subscriptionsListOptions,
  };
};
